/* Export instructions for podcast apps and RSS reader apps that support OPML export.
   Each step is an array of segments — plain text or clickable links. */

import type { PodcastApp, RssReaderApp } from "@/components/sources/NoSourcesEmptyState";

export interface StepSegment {
  text: string;
  url?: string;
}

export type Step = StepSegment[];

interface AppExportInstructions {
  label: string;
  steps: Step[];
}

/* ── Podcast apps ── */

export const OPML_CAPABLE_PODCAST_APPS: PodcastApp[] = [
  "apple-podcasts",
  "overcast",
  "castro",
  "pocket-casts",
];

export function isOpmlCapablePodcastApp(app: PodcastApp): boolean {
  return OPML_CAPABLE_PODCAST_APPS.includes(app);
}

/* ── RSS reader apps ── */

export const OPML_CAPABLE_RSS_APPS: RssReaderApp[] = [
  "feedly",
  "inoreader",
  "reeder",
  "newsblur",
  "netnewswire",
];

export function isOpmlCapableRssApp(app: RssReaderApp): boolean {
  return OPML_CAPABLE_RSS_APPS.includes(app);
}

/* ── Backward-compat alias ── */

export const isOpmlCapableApp = isOpmlCapablePodcastApp;

/* ── Instructions for all apps ── */

export const FEED_APP_INSTRUCTIONS: Record<string, AppExportInstructions> = {
  // Podcast apps
  "apple-podcasts": {
    label: "Apple Podcasts",
    steps: [
      [{ text: "Open Podcasts app on your Mac" }],
      [{ text: "Go to File → Export Library…" }],
      [{ text: "Save the .opml file to your computer" }],
      [{ text: "Upload the file below" }],
    ],
  },
  overcast: {
    label: "Overcast",
    steps: [
      [
        { text: "Go to " },
        { text: "overcast.fm/account", url: "https://overcast.fm/account" },
        { text: " in your browser" },
      ],
      [{ text: "Sign in with your Overcast account" }],
      [{ text: 'Click "Export OPML"' }],
      [{ text: "Save the file and upload it below" }],
    ],
  },
  castro: {
    label: "Castro",
    steps: [
      [{ text: "Open Castro on your iPhone" }],
      [{ text: "Go to Settings → Export Subscriptions" }],
      [{ text: "Choose OPML format" }],
      [{ text: "Send the file to your computer and upload below" }],
    ],
  },
  "pocket-casts": {
    label: "Pocket Casts",
    steps: [
      [
        { text: "Go to " },
        { text: "pocketcasts.com", url: "https://pocketcasts.com" },
        { text: " and sign in" },
      ],
      [{ text: "Click your profile → Settings" }],
      [{ text: "Find the Export section" }],
      [{ text: "Download OPML and upload it below" }],
    ],
  },

  // RSS reader apps
  feedly: {
    label: "Feedly",
    steps: [
      [
        { text: "Go to " },
        { text: "feedly.com", url: "https://feedly.com" },
        { text: " and sign in" },
      ],
      [{ text: "Click the gear icon → Organize Sources" }],
      [{ text: 'Click "Export to OPML"' }],
      [{ text: "Save the file and upload it below" }],
    ],
  },
  inoreader: {
    label: "Inoreader",
    steps: [
      [
        { text: "Go to " },
        { text: "inoreader.com", url: "https://www.inoreader.com" },
        { text: " and sign in" },
      ],
      [{ text: "Go to Preferences → Data Import/Export" }],
      [{ text: 'Click "Export" under OPML' }],
      [{ text: "Save the file and upload it below" }],
    ],
  },
  reeder: {
    label: "Reeder",
    steps: [
      [{ text: "Open Reeder on your Mac or iPhone" }],
      [{ text: "Go to Settings → Accounts → your account" }],
      [{ text: "Tap Export → OPML" }],
      [{ text: "Save or share the file and upload below" }],
    ],
  },
  newsblur: {
    label: "NewsBlur",
    steps: [
      [
        { text: "Go to " },
        { text: "newsblur.com", url: "https://newsblur.com" },
        { text: " and sign in" },
      ],
      [{ text: "Click the gear icon in the bottom-left" }],
      [{ text: 'Select "Export" and choose OPML' }],
      [{ text: "Save the file and upload it below" }],
    ],
  },
  netnewswire: {
    label: "NetNewsWire",
    steps: [
      [{ text: "Open NetNewsWire on your Mac" }],
      [{ text: "Go to File → Export Subscriptions…" }],
      [{ text: "Save the .opml file" }],
      [{ text: "Upload the file below" }],
    ],
  },
};

/** Alias kept for backward compat with PodcastImportInstructions */
export const PODCAST_APP_INSTRUCTIONS = FEED_APP_INSTRUCTIONS;
